<template>
  <div class="w-100 h-100 d-flex flex-column">
    <div class="layout-container" v-if="currentLayout.componentId == null">
      <div class="message">
        Please select one of the available visualizations:
      </div>
      <div class="component-cards">
        <div
          v-for="comp in availableComponents"
          :key="comp.id"
          class="component-card"
          @click="onUserSelectVisualization(comp)"
          :class="
            comp.type === 'chart'
              ? 'component-card-charts'
              : comp.type === 'table'
              ? 'component-card-tables'
              : 'component-card-news'
          "
        >
          <p>{{ comp.label }}</p>
          <span style="font-size: 0.8rem;">Click to explore</span>
        </div>
      </div>
    </div>
    <div
      class="row f-row w-100"
      v-else-if="localLayout.componentId !== 'newsWidget'"
    >
      <AssetSelector
        :assetTypes="assetTypes"
        :commoditySpecs="commoditySpecs"
        :currencySpecs="currencySpecs"
        :etfSpecs="etfSpecs"
        :futuresSpecs="futuresSpecs"
        :indexSpecs="indexSpecs"
        :irAvailableTerms="localLayout.variables.irAvailableTerms"
        :irSpecs="irSpecs"
        :isSpread="localLayout.componentId == 'commoditySpread'"
        :currentLayout="localLayout"
        :showLoadingComm="showLoadingComm"
        :showLoadingETF="showLoadingETF"
        :showLoadingFut="showLoadingFut"
        :showLoadingInd="showLoadingInd"
        :showLoadingIR="showLoadingIR"
        :componentNumber="componentNumber"
        :colorTheme="colorTheme"
        :userAssetType="userAssetType"
        :userSelectedAsset="userSelectedAsset"
        :userSelectedAsset2="userSelectedAsset2"
        :userSelectedTerm="userSelectedTerm"
        :verySmall="verySmall"
        :stopAssetType="stopAssetType"
        @eventOnChangeTerm="onChangeTerm"
        @eventOnUserClearComponent="onUserClearComponent"
        @eventOnSelectedAssetType="onSelectedAssetType"
        @eventOnUserAssetSelection="onUserAssetSelection"
        @eventOnUserAsset2Selection="onUserAsset2Selection"
        @eventOnUserShowFields="onUserShowFields"
        @eventOnUserAssetSelectionList="onUserAssetSelectionList"
      />
    </div>
    <div
      :style="
        localLayout.componentId === 'newsWidget'
          ? 'padding: 0; margin: 0; height: 100%;'
          : 'padding: 0; margin: 0; height: calc(100% - 40px)!important;'
      "
      v-if="localLayout.componentId != null"
    >
      <div
        v-if="localLayout.componentId == 'technicalChart'"
        class="w-100 h-100"
        style="padding: 0; margin: 0;"
      >
        <TechnicalAnalysis
          :analysisName="'Technical analysis'"
          :chartDataRaw="dataConverted"
          :chartNum="componentNumber"
          :chartName="'Chart' + componentNumber"
          :colorTheme="colorTheme"
          :commodityName="commodityName"
          :commodityStringLong="''"
          :dataReady="dataReady"
          :isCurrency="isCurrency"
          :nSelected="nSelected"
          :processData="processData"
          :selectedCurrency="selectedCommodityStr"
          :small="true"
          :showLoading="showLoading"
          :showLoadingSpot="showLoadingSpot"
          :spot="spot"
          :spotAnimate="spotAnimate"
          :spotFactor="spotFactor"
          :verySmall="true"
          :isDynamicSize="true"
          @eventOnUserClearComponent="onUserClearComponent"
        >
          <template v-slot:spot>
            <DetailsDisplay
              :analysisName="'Technical analysis'"
              :colorTheme="colorTheme"
              :showLoadingSpot="showLoadingSpot"
              :spot="spot"
              :spotAnimate="spotAnimate"
              :updateDate="updatedAt"
            />
          </template>
        </TechnicalAnalysis>
      </div>
      <div
        v-else-if="localLayout.componentId == 'commFuturesChart'"
        class="w-100 h-100"
        style="padding: 0; margin: 0;"
      >
        <CurveAnalysis
          :colorTheme="colorTheme"
          :commodityDefs="commoditySpecs[userSelectedAsset]"
          :isSmall="true"
          :processData="processData"
          :spot="spot"
          :verySmall="true"
        >
        </CurveAnalysis>
      </div>
      <div
        v-else-if="localLayout.componentId == 'commodityTable'"
        style="height: 100%; width:100%; padding: 0; margin: 0;"
      >
        <SingleCommodityTable
          :animateUp="animateUp"
          :animateDown="animateDown"
          :colorTheme="colorTheme"
          :commoditySpecs="commoditySpecs"
          :commodityTable="commodityTable"
          :componentNumber="componentNumber"
          :showFields="currentLayout['variables']['showFields']"
          :TAVisible="TAVisible"
          @eventOnUserShowTAChart="onUserShowTAChart"
        />
      </div>
      <div
        v-else-if="localLayout.componentId == 'commoditySpread'"
        style="height: 100%; width:100%; padding: 0; margin: 0;"
      >
        <SpreadTable
          :animateUp="animateUp"
          :animateDown="animateDown"
          :colorTheme="colorTheme"
          :spreadTable="spreadTable"
          :componentNumber="componentNumber"
          :showFields="currentLayout['variables']['showFields']"
          :TAVisible="TAVisible"
          @eventOnUserShowTAChart="onUserShowTAChart"
        />
      </div>
      <div
        v-else-if="
          localLayout.componentId == 'currencySpotTable' ||
            localLayout.componentId == 'assetsTable'
        "
        style="height: 100%; width:100%; padding: 0; margin: 0;"
      >
        <SpotTable
          :animateUp="animateUp"
          :animateDown="animateDown"
          :colorTheme="colorTheme"
          :spotTable="spotTable"
          :componentNumber="componentNumber"
          :showFields="currentLayout['variables']['showFields']"
          :TAVisible="TAVisible"
          @eventOnUserClearComponent="onUserClearComponent"
          @eventOnUserShowTAChart="onUserShowTAChart"
        />
      </div>
      <div
        v-else-if="localLayout.componentId == 'newsWidget'"
        style="height: 100%; width: 100%; padding: 0; margin: 0;"
      >
        <NewsWidget
          :colorTheme="colorTheme"
          :componentNumber="componentNumber"
          @eventOnUserClearComponent="onUserClearComponent"
        />
      </div>
      <template v-else> {{ localLayout }}, {{ componentNumber }} </template>
    </div>
  </div>
</template>

<script>
import AssetSelector from "@/components/overview/AssetSelector.vue";
import DetailsDisplay from "@/components/markets/MarketDetails/DetailsDisplay.vue";
import CurveAnalysis from "@/components/markets/CurveAnalysis";
import NewsWidget from "@/components/overview/NewsWidget.vue";
import SingleCommodityTable from "@/components/overview/SingleCommodityTable.vue";
import SpreadTable from "@/components/overview/SpreadTable.vue";
import SpotTable from "@/components/overview/SpotTable.vue";
import TechnicalAnalysis from "@/components/markets/TechnicalAnalysis.vue";
import { availableComponents, defaultMessage } from "@/_core/data/Overview.js";
import { formatDate_DDMMMYHMS } from "@/_core/_helpers/_helpers";

export default {
  name: "Layout1x1",
  components: {
    AssetSelector,
    DetailsDisplay,
    CurveAnalysis,
    NewsWidget,
    SingleCommodityTable,
    SpreadTable,
    SpotTable,
    TechnicalAnalysis,
  },
  props: {
    assetTypes: { required: true, default: () => [] },
    commoditySpecs: { required: true, default: () => [] },
    currencySpecs: { required: true, default: () => [] },
    etfSpecs: { required: true, default: () => [] },
    futuresSpecs: { required: true, default: () => [] },
    indexSpecs: { required: true, default: () => [] },
    irSpecs: { required: true, default: () => [] },
    nSelected: { required: false, default: 1 },
    showLoadingComm: { required: true, type: Boolean, default: false },
    showLoadingETF: { required: true, type: Boolean, default: false },
    showLoadingFut: { required: true, type: Boolean, default: false },
    showLoadingInd: { required: true, type: Boolean, default: false },
    showLoadingIR: { required: true, type: Boolean, default: false },
    stopAssetType: { required: true, type: Boolean, default: false },
    TAVisible: { required: true, type: Number, default: -1 },
    verySmall: { required: false, type: Boolean, default: false },
    // =======================================
    colorTheme: { required: true, type: Boolean, default: true },
    componentNumber: { required: true, default: 1 },
    allData: { required: true, default: () => [] },
    currentLayout: { required: true, default: () => ({ componentId: null }) },
    message: { required: true, default: () => defaultMessage },
    processData: { required: true },
  },
  data() {
    return {
      availableComponents,
      irAvailableTerms: [],
      isCurrency: false,
      localLayout: this.currentLayout,
      userAssetType: 1,
      userSelectedAsset: 17,
      userSelectedAsset2: 10,
      userSelectedTerm: null,
      // technical analysis
      commodityName: "N.A",
      dataConverted: [],
      dataReady: 0,
      defaultOperation: 0,
      selectedCommodityStr: "...",
      selectedCurrency: null,
      showLoading: true,
      showLoadingSpot: true,
      showLoadingSpotFactor: true,
      spot: null,
      spotAnimate: null,
      spotFactor: null,
      updatedAt: "...",
      // single commodity table
      animateUp: [],
      animateDown: [],
      commodityTable: [],
      selectedCommodity: 17,
      // spread table
      spreadTable: [],
      // spot table
      spotTable: [],
    };
  },
  created() {
    this.setupComponentVars();
    this.setupComponentData();
  },
  mounted() {
    this.setupComponentVars();
    this.setupComponentData();
  },
  computed: {},
  watch: {
    message: {
      handler(newVal) {
        this.messageHandler(newVal);
      },
    },
    currentLayout: {
      handler(newVal) {
        this.localLayout = newVal;
        this.setupComponentVars();
      },
      deep: true,
    },
    allData: {
      handler() {
        this.setupComponentData();
      },
      deep: true,
    },
  },
  methods: {
    onUserShowTAChart(val) {
      this.$emit("eventOnUserShowTAChart", val);
    },
    messageHandler(newVal) {
      if (newVal["close"] == null) {
        return;
      }
      if (this.localLayout.componentId == "technicalChart") {
        this.messageTechnicalAnalytis(newVal);
      } else if (this.localLayout.componentId == "commodityTable") {
        if (this.commodityTable.length == 0) {
          return;
        }
        this.messageCommodityTable(newVal);
      } else if (this.localLayout.componentId == "currencySpotTable") {
        if (this.spotTable.length == 0) {
          return;
        }
        this.messageCurrencySpotTable(newVal);
      } else if (this.localLayout.componentId == "commoditySpread") {
        if (this.spreadTable.length == 0) {
          return;
        }
        this.messageCommoditySpreadTable(newVal);
      } else if (this.localLayout.componentId === "commFuturesChart") {
        this.messageCommFuturesChart(newVal);
      }
    },
    messageCommFuturesChart(newVal) {
      if (newVal["close"] != null) {
        this.spot = +newVal["close"];

        this.messageCommodityTable(newVal);
      }
    },
    messageTechnicalAnalytis(newVal) {
      newVal["continuous_contract"] =
        newVal["continuous_contract"] == null
          ? 1
          : newVal["continuous_contract"];
      // commodities: stop if is no the selected contract
      if (this.userAssetType == 1) {
        const contr =
          this.localLayout?.variables?.continuous_contract ??
          this.commoditySpecs?.[this.userSelectedAsset]?.selected_contract;
        if (contr != newVal["continuous_contract"]) {
          return;
        }
      }
      if (newVal["close"] != null) {
        let _this = this;
        this.updatedAt = "Updated at: " + formatDate_DDMMMYHMS(newVal["time"]);
        this.previousSpot = +this.spot;
        this.spot = +newVal["close"];
        this.spotAnimate = Math.sign(this.spot - this.previousSpot);
        setTimeout(() => {
          _this.spotAnimate = 0;
        }, 1500);
      }
    },
    messageCommoditySpreadTable(newVal) {
      const tableIdx =
        Array.isArray(this.spreadTable) &&
        newVal &&
        typeof newVal.contract === "string"
          ? this.spreadTable[0]?.name === newVal.contract
            ? 0
            : this.spreadTable[1]?.name === newVal.contract
            ? 1
            : this.spreadTable[3].name[4] === newVal.contract
            ? 2
            : null
          : null;
      if (tableIdx == 0) {
        this.messageSpreadTable(newVal, 0);
      } else if (tableIdx == 1) {
        this.messageSpreadTable(newVal, 1);
      } else if (tableIdx == 2) {
        this.spreadTable[3]["data"].rate = +newVal["close"];
      }
      const emitVars = {
        alignedTable1: this.spreadTable[0]["data"],
        alignedTable2: this.spreadTable[1]["data"],
        componentNumber: this.componentNumber,
        constants: this.spreadTable[3]["data"],
      };
      this.emitSpreadTable(emitVars);
    },
    messageSpreadTable(newVal, idx) {
      const addIdx = idx == 0 ? 0 : this.spreadTable[0]["data"].length;
      const continuous_contract =
        newVal["continuous_contract"] == null
          ? 1
          : +newVal["continuous_contract"];
      if (continuous_contract > this.spreadTable[idx]["data"].length) {
        return;
      }
      const index = Math.max(
        0,
        this.spreadTable[idx]["data"].findIndex(
          (item) => item.continuous_contract === continuous_contract,
        ),
      );
      let row = this.spreadTable[idx]["data"][index];
      // animation
      const animateField =
        newVal["close"] >= row["close"] ? "animateUp" : "animateDown";
      if (!Array.isArray(this[animateField])) {
        this.$set(this, animateField, [index + addIdx]);
      } else if (!this[animateField].includes(index + addIdx)) {
        this.$set(this, animateField, [...this[animateField], index + addIdx]);
      }
      row["close"] = +newVal["close"];
      row["prev_close"] =
        newVal["prev_close"] == null
          ? row["prev_close"]
          : +newVal["prev_close"];
      row["change"] = row["close"] - row["prev_close"];
      row["high"] =
        newVal["high"] == null
          ? Math.max(+row["close"], +row["high"])
          : +newVal["high"];
      row["low"] =
        newVal["low"] == null
          ? Math.min(row["close"], row["low"])
          : +newVal["low"];
      row["observation_date"] = newVal["time"] ?? row["observation_date"];
      row["open"] = newVal["open"] == null ? row["open"] : +newVal["open"];
      row["pct_change"] = row["change"] / row["prev_close"];
      row["pct_change_5"] =
        (row["close"] - row["prev_close_5d"]) / row["prev_close_5d"];
      row["pct_change_10"] =
        (row["close"] - row["prev_close_10d"]) / row["prev_close_10d"];
      row["pct_change_20"] =
        (row["close"] - row["prev_close_20d"]) / row["prev_close_20d"];
      row["volume"] =
        newVal["volumes"] == null ? row["volume"] : +newVal["volumes"];
      row["openinterest"] =
        newVal["openinterest"] == null
          ? row["openinterest"]
          : +newVal["openinterest"];

      Object.assign(this.spreadTable[idx]["data"][index], row);
      setTimeout(() => {
        this.$set(
          this,
          animateField,
          this[animateField].filter((i) => i !== index + addIdx),
        );
      }, 1000);
    },
    messageCommodityTable(newVal) {
      const continuous_contract =
        newVal["continuous_contract"] == null
          ? 1
          : +newVal["continuous_contract"];
      if (continuous_contract > this.commodityTable.length) {
        return;
      }
      const index = Math.max(
        0,
        this.commodityTable.findIndex(
          (item) => item.asset === newVal["contract"],
        ),
      );
      const row = this.commodityTable.find(
        (item) => item.asset === newVal["contract"],
      );
      // animation
      const animateField =
        newVal["close"] >= row["close"] ? "animateUp" : "animateDown";
      if (!Array.isArray(this[animateField])) {
        this.$set(this, animateField, [index]);
      } else if (!this[animateField].includes(index)) {
        this.$set(this, animateField, [...this[animateField], index]);
      }
      row["close"] = +newVal["close"];
      row["prev_close"] =
        newVal["prev_close"] == null
          ? row["prev_close"]
          : +newVal["prev_close"];
      row["change"] = row["close"] - row["prev_close"];
      row["high"] =
        newVal["high"] == null
          ? Math.max(+row["close"], +row["high"])
          : +newVal["high"];
      row["low"] =
        newVal["low"] == null
          ? Math.min(row["close"], row["low"])
          : +newVal["low"];
      row["observation_date"] = newVal["time"] ?? row["observation_date"];
      row["open"] = newVal["open"] == null ? row["open"] : +newVal["open"];
      row["pct_change"] = row["change"] / row["prev_close"];
      row["pct_change_5"] =
        (row["close"] - row["prev_close_5d"]) / row["prev_close_5d"];
      row["pct_change_10"] =
        (row["close"] - row["prev_close_10d"]) / row["prev_close_10d"];
      row["pct_change_20"] =
        (row["close"] - row["prev_close_20d"]) / row["prev_close_20d"];
      row["volume"] =
        newVal["volumes"] == null ? row["volume"] : +newVal["volumes"];
      row["openinterest"] =
        newVal["openinterest"] == null
          ? row["openinterest"]
          : +newVal["openinterest"];

      Object.assign(this.commodityTable[index], row);
      setTimeout(() => {
        this.$set(
          this,
          animateField,
          this[animateField].filter((i) => i !== index),
        );
      }, 1000);
    },
    messageCurrencySpotTable(newVal) {
      const asset = newVal["contract"];
      let sectionIndex = -1;
      let index = -1;
      for (let i = 0; i < this.spotTable.length; i++) {
        const idx = this.spotTable[i].data.findIndex(
          (item) => item.asset === asset,
        );
        if (idx !== -1) {
          sectionIndex = i;
          index = idx;
          break;
        }
      }
      if (sectionIndex !== -1) {
        let row = this.spotTable[sectionIndex]["data"][index];
        // animation
        const animateField =
          newVal["close"] >= row["close"] ? "animateUp" : "animateDown";
        let fixedIndex =
          sectionIndex == 0
            ? index
            : sectionIndex == 1
            ? index + this.spotTable[0]["data"].length
            : index +
              this.spotTable[0]["data"].length +
              this.spotTable[1]["data"].length;
        if (!Array.isArray(this[animateField])) {
          this.$set(this, animateField, [`fixedIndex`]);
        } else if (!this[animateField].includes(fixedIndex)) {
          this.$set(this, animateField, [...this[animateField], fixedIndex]);
        }
        row["close"] = +newVal["close"];
        row["prev_close"] =
          newVal["prev_close"] == null
            ? row["prev_close"]
            : +newVal["prev_close"];
        row["change"] = row["close"] - row["prev_close"];
        row["high"] =
          newVal["high"] == null
            ? Math.max(+row["close"], +row["high"])
            : +newVal["high"];
        row["low"] =
          newVal["low"] == null
            ? Math.min(row["close"], row["low"])
            : +newVal["low"];
        row["observation_date"] = newVal["time"] ?? row["observation_date"];
        row["open"] = newVal["open"] == null ? row["open"] : +newVal["open"];
        row["pct_change"] = row["change"] / row["prev_close"];
        row["volume"] =
          newVal["volumes"] == null ? row["volume"] : +newVal["volumes"];

        Object.assign(this.spotTable[sectionIndex]["data"][index], row);
        setTimeout(() => {
          this.$set(
            this,
            animateField,
            this[animateField].filter((i) => i !== fixedIndex),
          );
        }, 1000);
      }
    },
    setupComponentData() {
      if (
        this.allData &&
        typeof this.allData === "object" &&
        Object.keys(this.allData).length === 0
      ) {
        this.dataConverted = [];
        this.commodityTable = [];
        return;
      }
      let selectedComponent = this.localLayout.componentId;

      if (selectedComponent == "technicalChart") {
        ({
          chartDataRaw: this.dataConverted,
          showLoading: this.showLoading,
          spot: this.spot,
          showLoadingSpot: this.showLoadingSpot,
          spotAnimate: this.spotAnimate,
        } = this.allData);
      } else if (selectedComponent == "commFuturesChart") {
        ({ spot: this.spot } = this.allData);
      } else if (selectedComponent == "commodityTable") {
        this.commodityTable = [...this.allData["commodityTable"]];
      } else if (selectedComponent == "commoditySpread") {
        this.spreadTable = [...this.allData["spreadTable"]];
      } else if (selectedComponent == "currencySpotTable") {
        this.spotTable = [...this.allData["spotTable"]];
      } else if (selectedComponent == "assetsTable") {
        this.spotTable = [...this.allData["spotTable"]];
      }
      // else if (selectedComponent == "currencyForward") {
      // }
    },
    setupComponentVars() {
      this.userAssetType = this.localLayout.variables.assetType;
      this.userSelectedAsset = this.localLayout.variables.selectedAsset;
      this.userSelectedTerm = this.localLayout.variables.selectedTerm;

      let selectedComponent = this.localLayout.componentId;
      if (selectedComponent == "technicalChart") {
        this.setupTechnicalChart(this.localLayout.variables);
      }
      // else if (selectedComponent == "commFuturesChart") {
      // }
      else if (selectedComponent == "commodityTable") {
        this.isCurrency = false;
        this.setupTechnicalChart(this.localLayout.variables);
        ({
          selectedCommodity: this.selectedCommodity,
        } = this.localLayout.variables);
      } else if (selectedComponent == "commoditySpread") {
        this.userSelectedAsset2 = this.localLayout.variables.selectedAsset2;
      } else if (selectedComponent == "currencySpotTable") {
        this.isCurrency = true;
      } else if (selectedComponent == "assetsTable") {
        this.isCurrency = false;
      }
      // else if (selectedComponent == "currencyForward") {
      // }
    },
    setupTechnicalChart(variables) {
      this.irAvailableTerms = variables["irAvailableTerms"];
      this.isCurrency = variables["assetType"] == 0;
      this.selectedCommodity = variables["selectedAsset"];
      if (this.selectedCommodity == null) {
        return;
      }

      if (variables["assetType"] === 0) {
        // currency
        let currDefs = this.currencySpecs[this.selectedCommodity];
        this.commodityName = currDefs.currency_name;
        this.selectedCommodityStr = currDefs.currency_name;
      } else if (variables["assetType"] === 1) {
        // commodity
        let selectedComm = this.selectedCommodity;
        let cmdtyDefs = this.commoditySpecs[selectedComm];

        if (Array.isArray(this.selectedCommodity)) {
          selectedComm = this.selectedCommodity[0];
          cmdtyDefs = this.commoditySpecs.find(
            (item) => item.pk_idcommodity === selectedComm,
          );
        }

        this.commodityName = cmdtyDefs.commodity_name;
        this.selectedCommodityStr =
          cmdtyDefs.commodity_name +
          " (" +
          (cmdtyDefs.is_cents ? "USc" : cmdtyDefs.fk_idcurrency) +
          "/" +
          cmdtyDefs.fk_idunit +
          ")";
        this.contract = cmdtyDefs.selected_contract;
        if (this.defaultOperation !== 0) {
          this.spotFactor = null;
          this.converted = false;
          this.factorUpdatedAt = null;
        }
      } else if (variables["assetType"] === 2) {
        // interest rate
        let irSpecs = this.irSpecs[this.selectedCommodity];
        if (irSpecs == undefined) {
          return;
        }
        const longName = (irSpecs && irSpecs.long_name) || "";
        this.commodityName = longName;
        this.selectedCommodityStr = longName;
      } else if (variables["assetType"] === 3) {
        // index
        let indexSpecs = this.indexSpecs[this.selectedCommodity];
        if (indexSpecs == undefined) {
          return;
        }
        const longName = (indexSpecs && indexSpecs.long_name) || "";
        this.commodityName = longName;
        this.selectedCommodityStr = longName;
      } else if (variables["assetType"] === 4) {
        // futures
        let futuresSpecs = this.futuresSpecs[this.selectedCommodity];
        if (futuresSpecs == undefined) {
          return;
        }
        const longName = (futuresSpecs && futuresSpecs.long_name) || "";
        this.commodityName = longName;
        this.selectedCommodityStr = longName;
      } else if (variables["assetType"] === 5) {
        // etf
        let etfSpecs = this.etfSpecs[this.selectedCommodity];
        if (etfSpecs == undefined) {
          return;
        }
        const longName = (etfSpecs && etfSpecs.long_name) || "";
        this.commodityName = longName;
      }
    },
    emitSpreadTable(emitVars) {
      this.$emit("emitSpreadTable", emitVars);
    },
    onUserClearComponent(val) {
      this.$emit("eventOnUserClearComponent", val);
    },
    onUserSelectVisualization(component) {
      this.$emit("eventOnUserSelectVisualization", {
        id: component.id,
        componentNumber: this.componentNumber,
      });
    },
    onSelectedAssetType(val) {
      this.$emit("eventOnSelectedAssetType", {
        assetType: val,
        componentNumber: this.componentNumber,
      });
    },
    onUserAssetSelection(val) {
      this.$emit("eventOnUserAssetSelection", {
        selectedAsset: val,
        componentNumber: this.componentNumber,
      });
    },
    onUserAssetSelectionList(val) {
      this.$emit("eventOnUserAssetSelectionList", {
        selectedAssets: val,
        componentNumber: this.componentNumber,
      });
    },
    onUserAsset2Selection(val) {
      this.$emit("eventOnUserAsset2Selection", {
        selectedAsset: val,
        componentNumber: this.componentNumber,
      });
    },
    onChangeTerm(val) {
      this.$emit("eventOnChangeTerm", {
        irSelectedTerm: val,
        componentNumber: this.componentNumber,
      });
    },
    onUserShowFields(val) {
      this.$emit("eventOnUserShowFields", val);
    },
  },
  beforeDestroy: function() {},
  filters: {},
};
</script>

<style scoped>
.message {
  font-size: 1.2rem;
}

.component-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 2rem;
}

.component-card {
  font-size: 1rem !important;
  flex: 1 1 80px;
  background: var(--background);
  border: 1px solid var(--textinactive);
  border-radius: 11px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: none !important;
  padding: 0.5rem;
  color: var(--textactive) !important;
  min-width: 112px;
  -moz-box-shadow: none;
}
.component-card-tables {
  border-bottom: 5px solid var(--expo);
}
.component-card-charts {
  border-bottom: 5px solid var(--hedge);
}
.component-card-news {
  border-bottom: 5px solid var(--dark-fwd);
}

.component-card.component-card-tables:hover {
  background: var(--expo-alpha);
  border: 1px solid var(--expo);
  border-bottom: 5px solid var(--expo);
  color: var(--textactive-2) !important;
  -moz-box-shadow: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}
.component-card.component-card-charts:hover {
  background: var(--hedge-alpha);
  border: 1px solid var(--hedge);
  border-bottom: 5px solid var(--hedge);
  color: var(--textactive-2) !important;
  -moz-box-shadow: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}
.component-card.component-card-news:hover {
  background: var(--dark-fwd-alpha);
  border: 1px solid var(--dark-fwd);
  border-bottom: 5px solid var(--dark-fwd);
  color: var(--textactive-2) !important;
  -moz-box-shadow: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.active .toggle__switch_g::after {
  left: 50%;
  border: 1px solid var(--expo);
  width: 50%;
  background: var(--expo);
  box-shadow: 0 0 1px var(--expo);
}

.toggle__button_g .toggle__switch_g::after,
.toggle__button_g .toggle__switch_g::before {
  content: "";
}
</style>
